import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import * as ls from 'local-storage'
import TextField from '@mui/material/TextField'
import LoadingButton from '@mui/lab/LoadingButton'
import type {} from '@mui/x-date-pickers/themeAugmentation'
import { Formik } from 'formik'
import * as Yup from 'yup'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAnglesRight } from '@fortawesome/free-solid-svg-icons'
import { AddFormHeading, DataTableFormFieldsGrid } from '../../../CropRotation/CropRotation.styles'
import { API } from '../../../../providers/API'
import { theme } from '../../../../theme/theme'
import { FuelInventoryItem } from './Fuel.types'

export const FuelInventoryDataEntryForm = ({
  rowData, replaceRowData, reloadData, toggleAddForm,
}: {
    rowData: FuelInventoryItem | null
    replaceRowData?: ({ newData } : {newData: FuelInventoryItem}) => void
    reloadData?: () => void
    toggleAddForm?: () => void
  }) => {
  const { t } = useTranslation()

  const [isSaving, setIsSaving] = useState<boolean>(false)

  const ValidationSchema = Yup.object().shape({
    petrolUsed: Yup.string().required(t('fuelInventory.validationMessages.petrolUsed')),
    dieselUsed: Yup.string().required(t('fuelInventory.validationMessages.dieselUsed')),
  })

  const separatorStyles = {
    borderBottom: `1px dotted ${theme.colors.blueUI}`,
    paddingBottom: theme.space.md,
    marginBottom: theme.space.md,
  }

  return (
    <Formik
      initialValues={{
        petrolUsed: rowData?.petrolUsed ?? '',
        dieselUsed: rowData?.dieselUsed ?? '',
      }}
      validationSchema={ValidationSchema}
      onSubmit={(values) => {
        setIsSaving(true)
        const formDataToSave = {
          ...rowData,
          petrolUsed: values.petrolUsed,
          dieselUsed: parseInt(values.dieselUsed.toString(), 10),
        }

        API.post('/fuelUsageConfirm', formDataToSave, { headers: { Authorization: `Bearer ${ls.get('loginCredentials')}` } }).then(
          ({ data }: {data: FuelInventoryItem}) => {
            if (reloadData) {
              reloadData()
            }
            if (toggleAddForm) {
              toggleAddForm()
            }
            if (replaceRowData) {
              replaceRowData({ newData: data })
            }
            setIsSaving(false)
          },
        ).catch(() => {
          setIsSaving(false)
        })
      }}
    >
      {({
        handleSubmit, errors, setFieldValue, values,
      }) => (
        <form
          onSubmit={handleSubmit}
          style={separatorStyles}
        >
          <AddFormHeading>{t('fuelInventory.editRowTitle')}</AddFormHeading>
          <DataTableFormFieldsGrid>
            <TextField
              label={t('fuelInventory.petrolUsed')}
              name="petrolUsed"
              value={values.petrolUsed}
              onChange={(newValue) => {
                setFieldValue('petrolUsed', newValue.target.value)
              }}
              fullWidth
              error={!!errors.petrolUsed}
              helperText={errors.petrolUsed ? errors.petrolUsed : ''}
            />
            <TextField
              label={t('fuelInventory.dieselUsed')}
              name="dieselUsed"
              value={values.dieselUsed}
              onChange={(newValue) => {
                setFieldValue('dieselUsed', newValue.target.value)
              }}
              fullWidth
              error={!!errors.dieselUsed}
              helperText={errors.dieselUsed ? errors.dieselUsed : ''}
            />
          </DataTableFormFieldsGrid>

          <LoadingButton
            variant="contained"
            type="button"
            color="primary"
            loading={isSaving}
            loadingPosition="start"
            endIcon={<FontAwesomeIcon icon={faAnglesRight} />}
            onClick={() => handleSubmit()}
          >
            {t('general.save')}
          </LoadingButton>
        </form>
      )}
    </Formik>
  )
}
